/*
 * <collection-infinite-scroll>
 *
 * Loads the next page of the collection grid (via the Section Rendering API)
 * and appends the products to the existing grid, either automatically when the
 * element scrolls into view ("auto" mode) or on click of the "Load more" button
 * ("button" mode).
 *
 * It cooperates with <navigation-element> (filters / sort / layout switcher):
 * that element replaces the grid + pagination HTML and then fires
 * `theme:navigation:reloaded`, which resets this element to the new page 1.
 */
class CollectionInfiniteScroll extends HTMLElement {
  static ROOT_MARGIN = '0px 0px 900px 0px';

  constructor() {
    super();

    this.onReloaded = () => this.reset();
    this.onClick = () => this.loadNextPage();
  }

  connectedCallback() {
    this.wrapper = this.closest('[data-body-wrapper]');
    if (!this.wrapper) return;

    this.grid = this.wrapper.querySelector('[data-body-grid]');
    this.pagination = this.wrapper.querySelector('[data-body-pagination]');
    if (!this.grid || !this.pagination) return;

    this.section_id = this.getAttribute('data-section-id');
    this.mode = this.getAttribute('data-mode') === 'button' ? 'button' : 'auto';
    this.status_template = this.getAttribute('data-status-template') || '';
    this.all_loaded_text = this.getAttribute('data-all-loaded-text') || '';

    this.button = this.querySelector('[data-infinite-button]');
    this.spinner = this.querySelector('[data-infinite-spinner]');
    this.status = this.querySelector('[data-infinite-status]');

    this.generation = 0;
    this.pages_loaded = 0;
    this.loading = false;
    this.failed = false;

    window.addEventListener('theme:navigation:reloaded', this.onReloaded);
    this.button?.addEventListener('click', this.onClick);
    this.observeGridBusy();
    this.reset();
  }

  disconnectedCallback() {
    window.removeEventListener('theme:navigation:reloaded', this.onReloaded);
    this.button?.removeEventListener('click', this.onClick);
    this.intersection_observer?.disconnect();
    this.busy_observer?.disconnect();
    this.abort_controller?.abort();
  }

  /*
   * <navigation-element> flags the grid with aria-busy while it fetches a new
   * result set. Any page we are loading at that moment belongs to the old
   * result set, so abort it. The reloaded event will re-initialise us.
   */
  observeGridBusy() {
    this.busy_observer?.disconnect();
    this.busy_observer = new MutationObserver(() => {
      if (this.grid.getAttribute('aria-busy') === 'true') {
        this.invalidate();
      }
    });
    this.busy_observer.observe(this.grid, { attributes: true, attributeFilter: ['aria-busy'] });
  }

  invalidate() {
    this.generation++;
    this.abort_controller?.abort();
    this.abort_controller = null;
    this.loading = false;
    this.failed = false;
    this.setLoading(false);
  }

  reset() {
    this.invalidate();
    this.pages_loaded = 0;
    this.next_url = this.findNextUrl(this.pagination);
    this.total = this.readTotal(this.wrapper);
    this.render();
    this.initIntersectionObserver();
  }

  findNextUrl(pagination) {
    const link = pagination?.querySelector('a[rel="next"]');
    if (!link) return null;

    const url = new URL(link.getAttribute('href'), window.location.origin);
    url.hash = '';
    return url;
  }

  readTotal(wrapper) {
    const total = parseInt(wrapper?.getAttribute('data-products-count'));
    return isNaN(total) ? null : total;
  }

  get product_count() {
    return this.grid.querySelectorAll('.product-card--root').length;
  }

  initIntersectionObserver() {
    this.intersection_observer?.disconnect();
    this.intersection_observer = null;

    if (this.mode !== 'auto' || !this.next_url) return;

    this.intersection_observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.loadNextPage();
        }
      },
      { rootMargin: CollectionInfiniteScroll.ROOT_MARGIN }
    );
    this.intersection_observer.observe(this);
  }

  /*
   * IntersectionObserver only fires on changes. If the sentinel is still
   * inside the root margin after a page was appended (short pages, tall
   * screens), re-observing forces a fresh callback so the next page loads too.
   */
  rearmIntersectionObserver() {
    if (!this.intersection_observer) return;

    this.intersection_observer.unobserve(this);
    if (this.next_url) {
      this.intersection_observer.observe(this);
    }
  }

  async loadNextPage() {
    if (this.loading || !this.next_url) return;
    if (this.grid.getAttribute('aria-busy') === 'true') return;

    const generation = this.generation;
    this.loading = true;
    this.failed = false;
    this.setLoading(true);

    this.abort_controller = new AbortController();
    const url = new URL(this.next_url);
    url.searchParams.set('section_id', this.section_id);

    try {
      const response = await fetch(url.toString(), { signal: this.abort_controller.signal });
      if (!response.ok) throw new Error(response.statusText);

      const html = await response.text();
      if (generation !== this.generation) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const wrapper = doc.querySelector('[data-body-wrapper]');
      const new_grid = wrapper?.querySelector('[data-body-grid]');
      const new_pagination = wrapper?.querySelector('[data-body-pagination]');
      if (!new_grid) throw new Error('Unexpected response');

      this.appendItems(new_grid);

      // Keep the (hidden) pagination in sync so it always points at the real next page.
      this.pagination.innerHTML = new_pagination ? new_pagination.innerHTML : '';
      this.next_url = this.findNextUrl(this.pagination);
      this.total = this.readTotal(wrapper) ?? this.total;
      this.pages_loaded++;

      this.loading = false;
      this.setLoading(false);
      this.render();

      if (typeof window.trigger === 'function') {
        window.trigger('theme:drawer:loadTriggers');
        window.trigger('theme:infiniteScroll:loaded', { section_id: this.section_id });
      }

      this.rearmIntersectionObserver();
    } catch (error) {
      if (error.name === 'AbortError' || generation !== this.generation) return;

      this.loading = false;
      this.failed = true;
      this.setLoading(false);
      this.render();
    }
  }

  appendItems(new_grid) {
    this.updateImageSizes(new_grid);

    // Blank fillers only make sense at the very end of the grid.
    this.grid.querySelectorAll(':scope > [data-item="blank"]').forEach((el) => el.remove());

    const items = [...new_grid.children].filter((el) => !el.classList.contains('main-collection--empty'));
    if (!items.length) return;

    const fragment = document.createDocumentFragment();
    items.forEach((el) => fragment.appendChild(el));
    this.grid.appendChild(fragment);

    this.revealItems(items);
  }

  /*
   * Mirrors Navigation.updateImageSizes(): when the 2-column layout is active
   * on a grid that supports more columns, cards use the alternate sizes attribute.
   */
  updateImageSizes(new_grid) {
    const columns = parseInt(this.grid.getAttribute('data-columns'));
    const alt_sizes = this.wrapper.getAttribute('data-alt-sizes');
    const max_sizes = this.wrapper.getAttribute('data-max-sizes');
    const sizes = columns === 2 ? alt_sizes : max_sizes;

    if (sizes) {
      new_grid.querySelectorAll('[sizes]').forEach((el) => el.setAttribute('sizes', sizes));
    }

    // Liquid marks the first row of every page as eager; appended rows are never above the fold.
    new_grid.querySelectorAll('img[loading="eager"]').forEach((el) => el.setAttribute('loading', 'lazy'));
  }

  /*
   * The theme's Transition class only knows about the items that existed when
   * the grid was rendered; appended items would stay invisible (opacity: 0).
   * Activate them here with the same horizontal cascade the grid uses, without
   * resetting the items that are already visible.
   */
  revealItems(items) {
    if (!this.grid.hasAttribute('data-transition-container')) return;

    const interval = parseInt(this.grid.getAttribute('data-transition-cascade-interval')) || 0;
    const transition_items = items.filter((el) => el.hasAttribute('data-transition-item'));
    if (!transition_items.length) return;

    requestAnimationFrame(() => {
      let column = 0;
      let previous_top = null;

      transition_items.forEach((el) => {
        const top = el.offsetTop;
        column = previous_top !== null && top === previous_top ? column + 1 : 0;
        previous_top = top;

        const target = el.hasAttribute('data-transition-cropper') ? el.children[0] : el;
        target?.style.setProperty('--transition--delay', `${column * interval}ms`);
        el.setAttribute('data-transition-active', true);
      });
    });
  }

  setLoading(loading) {
    this.setAttribute('aria-busy', loading);
    this.spinner?.setAttribute('aria-hidden', !loading);
    this.button?.setAttribute('aria-busy', loading);
    if (loading) this.setAttribute('data-state', 'loading');
  }

  render() {
    const has_next = !!this.next_url;
    const show_button = has_next && (this.mode === 'button' || this.failed);

    this.setAttribute('data-state', has_next ? 'idle' : 'done');
    this.button?.setAttribute('aria-hidden', !show_button);
    this.renderStatus(has_next);
  }

  /*
   * "Showing X of Y products" is only useful when the collection actually
   * spans more than one page; single-page collections show nothing here.
   */
  renderStatus(has_next) {
    if (!this.status) return;

    const count = this.product_count;
    const multi_page = has_next || this.pages_loaded > 0;
    let text = '';

    if (multi_page && count > 0 && this.total !== null && this.status_template) {
      text = this.status_template
        .replace('[count]', Math.min(count, this.total))
        .replace('[total]', this.total);
    }

    if (!has_next && this.pages_loaded > 0 && this.all_loaded_text) {
      text = text ? `${text} · ${this.all_loaded_text}` : this.all_loaded_text;
    }

    this.status.textContent = text;
  }
}

customElements.get('collection-infinite-scroll') ||
  customElements.define('collection-infinite-scroll', CollectionInfiniteScroll);
